import { Clock, ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { AsyncSelect } from "@/components/ui/async-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  type Account,
  useAccountSearch,
  useAccountsByIds,
} from "@/hooks/api/use-accounts";
import {
  type RecentAccount,
  useAccountSelection,
} from "@/hooks/use-account-selection";

// Union type to handle both full accounts and recent accounts
type AccountOption = Account | RecentAccount;

// Helper to determine if an option is a recent account (has timestamp)
const isRecentAccount = (account: AccountOption): account is RecentAccount => {
  return "timestamp" in account;
};

/**
 * Renders an account option with name, type, and a link to open in a new tab
 */
const renderAccountOption = (account: AccountOption) => (
  <div className="flex items-center justify-between w-full group">
    <div className="flex items-center gap-2 flex-1">
      {isRecentAccount(account) && (
        <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      )}
      <div className="flex flex-col min-w-0">
        <div className="font-medium">{account.name}</div>
        <div className="text-xs text-muted-foreground">{account.type}</div>
      </div>
    </div>
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={`/accounts/${account.id}/explorer`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.open(`/accounts/${account.id}/explorer`, "_blank");
          }}
          className="ml-2"
        >
          <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={5}>
        <p>Open in new tab</p>
      </TooltipContent>
    </Tooltip>
  </div>
);

/**
 * Account selector component
 *
 * Allows searching and selecting accounts with a dropdown.
 * Shows recent accounts and search results.
 */
export function AccountSelector() {
  const [search, setSearch] = useState<string>();
  const {
    selectedAccountId,
    initialAccount,
    handleAccountChange,
    recentAccounts,
  } = useAccountSelection();

  // Fetch accounts based on search
  const { data, isFetching } = useAccountSearch({
    search,
    enabled: !!search,
  });

  // Fetch selected account details if not found in recent accounts
  const { data: accountData } = useAccountsByIds(
    !initialAccount && selectedAccountId ? [selectedAccountId] : [],
  );

  // Determine which account to show as selected
  const selectedAccount = initialAccount ?? accountData?.results?.[0];

  // Add the selected account to recent accounts when coming from a URL
  useEffect(() => {
    if (!initialAccount && selectedAccountId && selectedAccount) {
      handleAccountChange(selectedAccount.id, selectedAccount);
    }
  }, [initialAccount, selectedAccountId, selectedAccount, handleAccountChange]);

  // Fetch handler for AsyncSelect component
  const fetcher = useCallback(
    async (
      searchValue?: string,
    ): Promise<{ results: AccountOption[]; hasMore: boolean }> => {
      // Update search value if it changed
      if (searchValue !== search) {
        setSearch(searchValue || undefined);
      }

      // Get search results if available
      const searchResults = data?.results ?? [];

      // Determine what to show based on search and results
      if (searchValue && searchResults.length > 0) {
        // Show search results
        return {
          results: searchResults,
          hasMore: false,
        };
      }

      // If we're searching but don't have results yet, return empty array
      // but the AsyncSelect will keep showing previous results until new ones arrive
      if (searchValue && isFetching) {
        return {
          results: [],
          hasMore: false,
        };
      }

      // Default to showing recent accounts
      return {
        results: recentAccounts,
        hasMore: false,
      };
    },
    [data, search, recentAccounts, isFetching],
  );

  return (
    <AsyncSelect<AccountOption>
      fetcher={fetcher}
      preload={true}
      renderOption={renderAccountOption}
      getOptionValue={(account) => account.id}
      getDisplayValue={(account) => account.name}
      selectedOption={selectedAccount}
      label="Accounts"
      placeholder="Select account..."
      searchPlaceholder="Search by name or source record ID"
      value={selectedAccountId ?? ""}
      onChange={handleAccountChange}
      isSearching={!!search && isFetching}
      triggerClassName="bg-white dark:bg-[hsl(195_30%_12%)]"
      notFound={
        <div className="py-6 text-center text-sm">
          {isFetching ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching accounts...</span>
            </div>
          ) : search ? (
            "No accounts found"
          ) : (
            "No recent accounts"
          )}
        </div>
      }
      loadingSkeleton={
        <div className="p-4">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-4 w-4/5 animate-pulse rounded-lg corner-smooth-lg bg-muted" />
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
